//eslint-disable-next-line
//@ts-nocheck
import {
  FilledAreaType,
  LineStudyPlotStyle,
  StudyInputType,
  StudyPlotType,
  StudyTargetPriceScale,
} from "@/lib/datafeed/tvTypes.ts";

import {
  CustomIndicator,
  PineJS,
  RawStudyMetaInfoId,
} from "@tradingView/charting_library";

import {
  GetPriceSeries,
  MovingAverage,
  AccessCheck,
} from "@/lib/indicators/indicators-common-functions";

import { IIndicatorOptions } from "@/domain/interfaces/general.interface";

export const keltnerChannel = (
  PineJS: PineJS,
  options?: IIndicatorOptions,
): CustomIndicator => {
  return {
    name: "Keltner channel",
    metainfo: {
      _metainfoVersion: 51,
      id: "keltner-channel@tv-basicstudies-1" as RawStudyMetaInfoId,
      description: "[SFT] Keltner channel",
      shortDescription: "[SF] Keltner channel",
      is_hidden_study: false,
      is_price_study: true,
      isCustomIndicator: true,
      format: {
        type: "price",
        precision: 2,
      },
      plots: [
        { id: "maPlot", type: StudyPlotType.Line },
        { id: "upperBandPlot", type: StudyPlotType.Line },
        { id: "lowerBandPlot", type: StudyPlotType.Line },
      ],
      filledAreas: [
        {
          id: "Background",
          objAId: "upperBandPlot",
          objBId: "lowerBandPlot",
          title: "Background",
          type: FilledAreaType.TypePlots,
        },
      ],
      defaults: {
        filledAreasStyle: {
          Background: {
            color: "rgba(82, 237, 106, 0.1)",
            visible: true,
            transparency: 20,
          },
        },
        styles: {
          maPlot: {
            linestyle: 0,
            visible: true,
            linewidth: 1,
            plottype: LineStudyPlotStyle.Line,
            trackPrice: false,
            color: "rgb(255, 160, 40)",
            transparency: 40,
          },
          upperBandPlot: {
            linestyle: 0,
            visible: true,
            linewidth: 1,
            plottype: LineStudyPlotStyle.Line,
            trackPrice: false,
            color: "rgb(13, 157, 219)",
          },
          lowerBandPlot: {
            linestyle: 0,
            visible: true,
            linewidth: 1,
            plottype: LineStudyPlotStyle.Line,
            trackPrice: false,
            color: "rgb(13, 157, 219)",
          },
        },
        precision: 2,
        inputs: {
          sourceInput: "close",
          maTypeInput: "SMA",
          lengthInput: 240,
          atrMlpInput: 2.0,
        },
      },
      styles: {
        maPlot: {
          title: "Moving average",
          histogramBase: 0,
        },
        upperBandPlot: {
          title: "Upper band",
          histogramBase: 0,
        },
        lowerBandPlot: {
          title: "Lower band",
          histogramBase: 0,
        },
      },
      inputs: [
        {
          id: "sourceInput",
          name: "Price source",
          defval: "close",
          options: ["open", "high", "low", "close", "hl2", "hlc3", "ohlc4"],
          type: StudyInputType.Text,
        },
        {
          id: "maTypeInput",
          name: "MA type",
          defval: "SMA",
          options: ["SMA", "EMA", "WMA", "VWMA", "LSMA"],
          type: StudyInputType.Text,
        },
        {
          id: "lengthInput",
          name: "Length",
          defval: 240,
          type: StudyInputType.Integer,
        },
        {
          id: "atrMlpInput",
          name: "ATRs",
          defval: 3.0,
          type: StudyInputType.Float,
        },
      ],
    },

    constructor: function () {
      this.init = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;
      };

      this.main = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;

        // User input
        const [sourceInput, maTypeInput, lengthInput, atrMlpInput]: [
          string,
          string,
          number,
          number,
        ] = Array.from({ length: 4 }, (_, i) => this._input(i));

        // Data parsing
        const sourceSeries: any = GetPriceSeries(
          PineJS,
          sourceInput,
          this._context,
        );

        // Calculations
        const ma: number = MovingAverage(
          PineJS,
          sourceSeries,
          maTypeInput,
          lengthInput,
          this._context,
        );
        const atr: number = PineJS.Std.atr(lengthInput, this._context);
        const upperBand: number = ma + atrMlpInput * atr;
        const lowerBand: number = ma - atrMlpInput * atr;

        const shouldShowData: boolean = AccessCheck(
          PineJS,
          this._context,
          options,
        );

        if (shouldShowData) {
          return [ma, upperBand, lowerBand];
        } else {
          return [NaN, NaN, NaN];
        }
      };
    },
  };
};
